import * as jp from "jsonpath";

type Key = string | number | boolean;

type KeyOf<T> = Extract<keyof T, Key>;

type PathsInternal<T, Path extends string = ""> = T extends Record<
  string,
  unknown
>
  ? {
      [K in KeyOf<T>]: PathsInternal<
        T[K],
        Path extends "" ? K : `${Path}.${K}`
      >;
    }[KeyOf<T>]
  : T extends Array<unknown>
  ? PathsInternal<T[0], Path extends "" ? "*" : `${Path}.*`>
  : T extends number
  ? Path
  : never;

type Paths<T> = PathsInternal<T>;

export function millisecondsToMinutes<O extends Record<string, unknown>>(
  object: O,
  paths: Paths<O>[],
): O {
  const clone = JSON.parse(JSON.stringify(object));
  paths.forEach((path: Key) => {
    jp.apply(clone, path.toString(), (value: number) => {
      if (typeof value !== "number") {
        return value;
      }
      if (!Number.isSafeInteger(value) || value % 60_000 !== 0) {
        throw new Error("Invalid milliseconds");
      }
      return value / 60_000;
    });
  });
  return clone;
}

export function minutesToMilliseconds<O extends Record<string, unknown>>(
  object: O,
  paths: Paths<O>[],
): O {
  const clone = JSON.parse(JSON.stringify(object));
  paths.forEach((path: Key) => {
    jp.apply(clone, path.toString(), (value: number) => {
      if (typeof value !== "number") {
        return value;
      }
      if (!Number.isSafeInteger(value)) {
        throw new Error("Invalid minutes");
      }
      return value * 60_000;
    });
  });
  return clone;
}

export function secondsToHours<O extends Record<string, unknown>>(
  object: O,
  paths: Paths<O>[],
): O {
  const clone = JSON.parse(JSON.stringify(object));
  paths.forEach((path: Key) => {
    jp.apply(clone, path.toString(), (value: number) => {
      if (typeof value !== "number") {
        return value;
      }
      if (!Number.isSafeInteger(value)) {
        throw new Error("Invalid seconds");
      }
      return value / 3_600;
    });
  });
  return clone;
}

export function hoursToSeconds<O extends Record<string, unknown>>(
  object: O,
  paths: Paths<O>[],
): O {
  const clone = JSON.parse(JSON.stringify(object));
  paths.forEach((path: Key) => {
    jp.apply(clone, path.toString(), (value: number) => {
      if (typeof value !== "number") {
        return value;
      }
      if (!Number.isSafeInteger(value)) {
        throw new Error("Invalid hours");
      }
      return value * 3_600;
    });
  });
  return clone;
}
